import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, CalendarDays, TrendingUp, ArrowLeft, X, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { lvl } from "@/lib/levels";

const RANGES = [14, 28, 42];
const today = () => new Date().toISOString().slice(0, 10);

export default function AdminCalendar() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [start, setStart] = useState(today());
  const [days, setDays] = useState(28);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // date string
  const [drafts, setDrafts] = useState({}); // class_id -> blocked value
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      setData((await api.get("/admin/availability", { params: { start, days } })).data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [start, days, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const selectedDay = data?.calendar?.find((d) => d.date === selected);

  const openDay = (day) => {
    setSelected(day.date);
    setDrafts(Object.fromEntries(day.classes.map((c) => [c.class_id, c.blocked])));
  };

  const saveOverride = async (classId) => {
    try {
      await api.post("/admin/availability/override", { date: selected, class_id: classId, blocked: Number(drafts[classId]) || 0 });
      toast.success(t("admin.saved"));
      const { data: fresh } = await api.get("/admin/availability", { params: { start, days } });
      setData(fresh);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!isAdmin)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <p className="mx-auto max-w-3xl px-5 py-24 text-slate-600" data-testid="calendar-no-access">{t("admin.noAccess")}</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-12 lg:px-10">
        <Link to="/admin" data-testid="calendar-back-btn" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors duration-200 hover:text-sea">
          <ArrowLeft size={15} /> {t("admin.title")}
        </Link>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{t("admin.title")}</p>
            <h1 className="mt-2 flex items-center gap-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="calendar-title">
              <CalendarDays size={22} className="text-sea" /> {t("fc.calendar")}
            </h1>
            {data && <p className="mt-2 text-sm text-slate-600">{t("fc.fleet")}: <b>{data.fleet_total}</b></p>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input type="date" data-testid="calendar-start-input" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
            <select data-testid="calendar-range-select" value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
              {RANGES.map((r) => <option key={r} value={r}>{r} {t("fc.days")}</option>)}
            </select>
            <Button variant="outline" data-testid="calendar-refresh-btn" onClick={load}>
              <RefreshCw size={15} className="mr-1.5" /> {t("common.refresh")}
            </Button>
            <Link to="/admin/forecast" data-testid="to-forecast-link">
              <Button className="bg-sea hover:bg-sea-dark"><TrendingUp size={15} className="mr-1.5" /> {t("fc.forecast")}</Button>
            </Link>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-600" data-testid="calendar-legend">
          {["critical", "busy", "moderate", "quiet"].map((l) => (
            <span key={l} className="inline-flex items-center gap-1.5">
              <span className={`h-3 w-3 rounded-full ${lvl(l).dot}`} /> {t(`fc.levels.${l}`)}
            </span>
          ))}
        </div>

        {loading && !data && <p className="mt-10 text-slate-500" data-testid="calendar-loading">{t("common.loading")}</p>}

        {data && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7" data-testid="calendar-grid">
            {data.calendar.map((d) => {
              const s = lvl(d.level);
              return (
                <button
                  key={d.date}
                  data-testid={`calendar-day-${d.date}`}
                  onClick={() => openDay(d)}
                  className="rounded-xl border border-slate-200 bg-white p-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-sea/50 hover:shadow-md"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-900">{d.date.slice(5)}</span>
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-400">{d.weekday.slice(0, 3)}</p>
                  <div className={`mt-2 h-1.5 overflow-hidden rounded-full ${s.track}`}>
                    <div className={`h-full rounded-full ${s.bar}`} style={{ width: `${Math.min(100, d.utilisation_pct)}%` }} />
                  </div>
                  <p className="mt-2 text-sm font-medium text-slate-900">{Math.round(d.utilisation_pct)}%</p>
                  <p className="text-[11px] text-slate-500">{d.vehicles_needed}/{d.capacity}</p>
                  {d.blocked_total > 0 && <p className="mt-1 text-[11px] font-medium text-rose-600">{d.blocked_total} {t("fc.blocked")}</p>}
                  {d.drivers?.length > 0 && <p className="mt-1 text-xs">{d.drivers.map((x) => x.emoji).join(" ")}</p>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Day detail / override modal */}
      {selectedDay && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 p-4" data-testid="calendar-day-modal" onClick={() => setSelected(null)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 ambient" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="label-eyebrow">{selectedDay.weekday}</p>
                <h2 className="mt-1 font-display text-2xl">{selectedDay.date}</h2>
                <p className="mt-1 text-sm text-slate-600">
                  {Math.round(selectedDay.utilisation_pct)}% · {selectedDay.vehicles_needed}/{selectedDay.capacity} {t("fc.needed")}
                  {selectedDay.drivers?.length > 0 && <span className="ml-2">{selectedDay.drivers.map((x) => `${x.emoji} ${x.name}`).join(", ")}</span>}
                </p>
              </div>
              <button data-testid="calendar-modal-close" onClick={() => setSelected(null)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2">{t("ops.vclass")}</th>
                    <th className="px-2 py-2 text-center">{t("fc.needed")}</th>
                    <th className="px-2 py-2 text-center">{t("fc.available")}</th>
                    <th className="px-2 py-2 text-center">{t("fc.blocked")}</th>
                    <th className="px-2 py-2 text-right">{t("fc.override")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="calendar-class-table">
                  {selectedDay.classes.map((c) => (
                    <tr key={c.class_id} data-testid={`calendar-class-${c.class_id}`}>
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-800">{c.name}</p>
                        <p className="text-[11px] text-slate-400">{t("ops.active")}: {c.fleet_size} · {t("fc.booked")}: {c.booked}</p>
                      </td>
                      <td className="px-2 py-2 text-center">{c.needed}</td>
                      <td className={`px-2 py-2 text-center font-medium ${c.available <= c.needed ? "text-rose-600" : "text-emerald-600"}`}>{c.available}</td>
                      <td className="px-2 py-2 text-center">{c.blocked}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-end gap-1.5">
                          <Input
                            type="number"
                            min="0"
                            max={c.fleet_size}
                            data-testid={`override-input-${c.class_id}`}
                            value={drafts[c.class_id] ?? c.blocked}
                            onChange={(e) => setDrafts({ ...drafts, [c.class_id]: e.target.value })}
                            className="h-8 w-16"
                          />
                          <Button size="sm" variant="outline" data-testid={`override-save-${c.class_id}`} onClick={() => saveOverride(c.class_id)}>
                            <Ban size={13} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-slate-500">{t("fc.overrideHint")}</p>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
